/**
 * @format
 * @module lib/notifications/user-notifications
 */
import tgamConfig from "config/tgam";
import logger from "utils/logger";
import { qs, getProp, relativeUrl } from "utils/utils";
import {
  websiteAlert,
  websiteAlertDismissListener,
  websiteAlertNodeSelector
} from "ui/templates/notifications";
import { notifications, isAnonymous } from "lib/identity/api";
import { isRedArticle } from "utils/story";
import consts from "lib/consts";
import values from "lodash/values";
import ee from "lib/eventemitter";
import { session, isLocalStorageTypeSupported } from "lib/store";

const debugTag = "[USER-NOTIFICATIONS]";

// Key for local storage item that keeps track of user notifications that have been dismissed.
const storeDismissed = "tgam.notifications.userAlertsDismissed";

/**
 * Initializes user notification(s)
 */
export default function init() {
  const rules = {
    notPbAdmin: !tgamConfig.env.isPbAdmin,
    notAnonymous: !isAnonymous()
  };

  const enabled = values(rules).every(item => item === true);

  logger.info(`${debugTag} init`, enabled, rules);
  if (!enabled) {
    return;
  }

  // Get notifications for logged in user
  let notes = getProp(tgamConfig, "datalayer.identity.userNotifications", []);

  // Optional url search param for debugging notification display
  const params = new URLSearchParams(document.location.search);
  const un = params.get("un") || "";
  if (un.includes("a")) {
    notes.push({ type: notifications.abuser, frequency: 1 }); // website abuser
  }
  if (un.includes("s")) {
    notes.push({ type: notifications.suspension }); // user suspension
  }
  logger.info(`${debugTag} notes:`, notes);

  // Let's use this global alert placement, and insert user abuse alert above it.
  let websiteAlertNode = qs(websiteAlertNodeSelector);
  websiteAlertNode = websiteAlertNode && websiteAlertNode.parentElement;

  // Portfolio suspension wall replaces the realtime control feature and the financial barchart feature.
  const isPortfolio = /markets\/(portfolio)/.test(tgamConfig.meta.siteName);
  const portolioNode = isPortfolio ? qs(".pb-f-financial-realtime-control") : null;

  // Article suspension wall replaces the usual article content body.
  const isRedStory = isRedArticle();
  const articleNode = isRedStory ? qs(".js-c-article-body") : null;

  const swapContent = isRedStory || isPortfolio; // custom placement
  let target; // placement of message

  notes.forEach(note => {
    let message;

    if (note.type === notifications.abuser) {
      target = websiteAlertNode;
      message = renderAbuseAlert();
    } else if (note.type === notifications.suspension) {
      if (!wasDismissed("note-suspension")) {
        target = isRedStory ? articleNode : isPortfolio ? portolioNode : websiteAlertNode;
        message = renderSuspension(isRedStory ? "wall" : "alert");
      }
    }

    if (target) {
      if (swapContent) {
        target = target.parentElement;
        target.innerHTML = ""; // remove existing content
        target.insertAdjacentHTML("afterbegin", message);
      } else {
        target.insertAdjacentHTML("beforebegin", message);
      }
    }
  });

  if (!isRedStory && target) {
    const container = target.closest(".pb-container");
    websiteAlertDismissListener(container, ".js-note-suspension__dismiss-btn", saveDismissed);
  }
}

/**
 * Check if user notification has been dismissed during this web session.
 * @param {String} id - of notification
 * @returns {Boolean}
 */
export function wasDismissed(id) {
  const hasSessionStorage = isLocalStorageTypeSupported("sessionStorage");
  if (!hasSessionStorage) {
    return false;
  }
  const db = session.get(storeDismissed);
  const result = db ? db.includes(id) : false;
  return result;
}

/**
 * Records user notification with alertId as being dismissed to session storage.
 * @param {String} id - of notification
 */
export function saveDismissed(id) {
  const hasSessionStorage = isLocalStorageTypeSupported("sessionStorage");
  if (!hasSessionStorage) {
    return;
  }
  const db = session.get(storeDismissed);
  const shouldAdd = Array.isArray(db) && !db.includes(id);
  const newData = shouldAdd ? [...db, id] : [id];
  session.set(storeDismissed, newData);
}

/**
 * Removes user notification with alertId as being dismissed from session storage.
 * @param {String} id - of notification
 */
export function clearDismissed(id) {
  const hasSessionStorage = isLocalStorageTypeSupported("sessionStorage");
  if (!hasSessionStorage) {
    return;
  }
  const db = session.get(storeDismissed);
  const shouldRemove = Array.isArray(db) && db.includes(id);
  const newData = shouldRemove ? db.filter(i => i !== id) : db;
  session.set(storeDismissed, newData);
}

/**
 * Generate the user abuse note markup.
 * @returns {String} of HTML
 */
function renderAbuseAlert() {
  const Feature = "Website abuse";
  const feature = Feature.toLowerCase();

  window.analyticsLayer.push({
    event: "simpleEvent",
    simpleEvent: {
      action: "message",
      label: `${Feature} message is shown`
    }
  });

  // subscription terms link
  const termsUrl = relativeUrl("/privacy-terms/terms-and-conditions/#tgamtos5");
  const termsClick = JSON.stringify({
    feature,
    type: "link",
    label: "subscription terms",
    position: 1,
    hierarchy: 1
  });

  // group subscriptions link
  const groupUrl = relativeUrl("/globe-group-subscriptions/");
  const groupClick = JSON.stringify({
    feature,
    type: "link",
    label: "group subscriptions",
    position: 2,
    hierarchy: 1
  });

  // log out link
  const logoutClick = JSON.stringify({
    feature,
    type: "link",
    label: "log out",
    position: 3,
    hierarchy: 1
  });

  const html = `
    <p>
      Your account appears to be in use on more than 4 devices, which violates our
      <a href="${termsUrl}" data-analyticsclick='${termsClick}'>subscription terms</a>. If you
      require group access, please contact
      <a href="${groupUrl}" data-analyticsclick='${groupClick}'>Group Subscriptions</a>.
      <a href="${consts.URLS.LOGOUT}" data-analyticsclick='${logoutClick}'>Log out</a>
    </p>
  `;

  // I don't think analytics would want the full label, let's truncate it.
  ee.trigger("analytics.trackGTMEvent", [
    {
      origin: "api",
      feature,
      label: "Your account appears to be in use on more than 4 devices"
    },
    "message"
  ]);

  const alert = websiteAlert({
    id: "js-note-abuser",
    type: "system",
    classes: ["u-space-mts", "u-space-mbs"],
    content: html,
    wrap: "u-wrapper"
  });
  return alert;
}

/**
 * Generate the user suspense note markup.
 * @param {"wall" | "alert"} type - of suspension displayed
 * @returns {String} of HTML
 */
function renderSuspension(type = "") {
  const Feature = "Account suspension";
  const feature = Feature.toLowerCase();

  window.analyticsLayer.push({
    event: "simpleEvent",
    simpleEvent: {
      action: "message",
      label: `${Feature} message is shown`
    }
  });

  // terms and conditions link
  const termsUrl = relativeUrl("/privacy-terms/terms-and-conditions/");
  const termsClick = JSON.stringify({
    feature,
    type: "link",
    label: "subscription terms",
    position: 1,
    hierarchy: 1
  });

  // subscription settings link
  const updateUrl = consts.URLS.ACCOUNT.SUBSCRIPTIONS;

  const updateLinkData = {
    feature,
    type: "link",
    label: "subscription settings",
    position: 2,
    hierarchy: 1
  };
  const updateLinkClick = JSON.stringify(updateLinkData);

  const updateBtnData = {
    feature,
    type: "button",
    label: "subscription settings",
    action: "Update Subscription"
  };
  const updateBtnClick = JSON.stringify(updateBtnData);

  // Content has diff spacing based on type
  const spacer = type === "alert" ? " " : "<br /><br />";

  // Shared title/subtitle display
  const title = "Access digital content";
  const subtitle = type === "alert" ? `<strong>${title}.</strong> ` : "";

  const message = `
    ${subtitle}We've updated our
    <a href="${termsUrl}" data-analyticsclick='${termsClick}'>Terms &amp; Conditions</a>. Since you
    are not being charged while your delivery is suspended, you are not able to access all digital
    content/features.${spacer}To continue reading, you will need to update your
    <a href="${updateUrl}" data-analyticsclick='${updateLinkClick}'>subscription settings</a> or
    call Customer Care at 1-800-387-5400.
  `;

  const update = `<a
    href="${updateUrl}"
    class="c-button c-button--grey-secondary c-button--no-border"
    data-analyticsclick='${updateBtnClick}'
  >
    <span class="c-button__text">UPDATE SUBSCRIPTION</span>
  </a>`;

  const alertId = "note-suspension";
  const jsId = `js-${alertId}`;

  const dismissClasses = [
    "c-button c-button--icon-only",
    "c-button--small",
    "c-button--no-border",
    "c-button--transparent-darken",
    "c-website-alert__dismiss-btn",
    jsId + "__dismiss-btn"
  ].join(" ");

  const dismiss = /* HTML */ `<button
    class="${dismissClasses}"
    aria-label="Close alert"
    aria-controls="${jsId}"
  >
    <svg role="img" pointer-events="none" focusable="false" aria-hidden="true">
      <use xlink:href="${tgamConfig.env.svgSpritePath}#close-x"></use>
    </svg>
  </button>`;

  const alert = websiteAlert({
    id: jsId,
    alertId: alertId,
    type: type === "alert" ? "suspension" : "wall",
    classes: type === "alert" ? ["u-space-mts", "u-space-mbs"] : ["u-space-mbs"],
    title: type === "alert" ? undefined : title,
    content: `${message}<br />${update}`,
    foot: type === "alert" ? dismiss : undefined,
    wrap: "u-wrapper"
  });
  return alert;
}
